import { useQuery } from "@tanstack/react-query";

import { liveMeta, makeApi, type FreshnessMeta } from "./api";
import { AdminProvider, meQuery } from "./context";
import { CustomersTab } from "./CustomersTab";
import { openCount, queueQuery, QueueTab } from "./QueueTab";
import { ReferralRulesTab } from "./ReferralRulesTab";
import { StaffChangesTab } from "./StaffChangesTab";

/**
 * Precision Admin, rendered natively in the portal: the rewrite of the docked Worker
 * (Ketosource/ketosource-app-admin-panel src/app.js) on the Cloud Run API in that repo's api/.
 * One component, two registrations: `precision_admin` reads the live app, `precision_admin_dev` (Tech area,
 * "Precision Admin (DEV)", Damien 31 Oct 2026) the DEV app, each through its own same-origin proxy
 * (functions/precision_admin*). Only the module key and base differ, so the two cannot drift. The portal
 * rail and the shell's tabs are the only navigation: no inner sidebar (Damien, 16 Oct 2026).
 *
 * `decision` is deliberately unread (I-7): `ShellPage` has already wrapped this in `PermissionGate`.
 * Writes show only when the API says they are on (`/api/me` writes), exactly as the Worker did.
 */
const BASES = {
  precision_admin: "/precision_admin",
  precision_admin_dev: "/precision_admin_dev",
} as const;

/**
 * The freshness bridge. Every view reads the app's database live on each request, so the date the
 * shell shows is the moment of the read (the `/api/me` call every tab shares).
 */
function useMeta(moduleKey: keyof typeof BASES, enabled: boolean): FreshnessMeta | null | undefined {
  const api = makeApi(BASES[moduleKey]);
  const me = useQuery({ ...meQuery(moduleKey, api), enabled });
  if (!enabled) return undefined;
  return me.data ? liveMeta(me.dataUpdatedAt, me.data.env) : undefined;
}
/**
 * The tab bar's hints (registry `useNativeModuleTabHints`, v3.1 PA1): the queue tab carries its open count,
 * and the module opens on the queue while something waits, else on Customers. Same cache as QueueTab.
 */
function useTabHints(moduleKey: keyof typeof BASES, enabled: boolean) {
  const api = makeApi(BASES[moduleKey]);
  const q = useQuery({ ...queueQuery(moduleKey, api), enabled });
  if (!enabled || !q.data) return undefined;
  const n = openCount(q.data);
  return { counts: { queue: n }, preferred: n > 0 ? "queue" : "customers" };
}
export const usePrecisionAdminTabHints = (enabled: boolean) => useTabHints("precision_admin", enabled);
export const usePrecisionAdminDevTabHints = (enabled: boolean) => useTabHints("precision_admin_dev", enabled);

export const usePrecisionAdminMeta = (enabled: boolean) => useMeta("precision_admin", enabled);
export const usePrecisionAdminDevMeta = (enabled: boolean) => useMeta("precision_admin_dev", enabled);

type Props = {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
};

function Tabs({ activeTabKey }: { activeTabKey: string | null }) {
  if (activeTabKey === "queue") return <QueueTab />;
  if (activeTabKey === "overrides") return <StaffChangesTab />;
  if (activeTabKey === "referral_rules") return <ReferralRulesTab />;
  return <CustomersTab />;
}

function moduleFor(moduleKey: keyof typeof BASES) {
  return function PrecisionAdmin({ activeTabKey }: Props) {
    return (
      <AdminProvider moduleKey={moduleKey} base={BASES[moduleKey]}>
        <Tabs activeTabKey={activeTabKey} />
      </AdminProvider>
    );
  };
}

export const PrecisionAdminModule = moduleFor("precision_admin");
export const PrecisionAdminDevModule = moduleFor("precision_admin_dev");

